import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, LogOut, LayoutDashboard, Trash2, Calculator, Check, Printer, Plus, Pencil, X } from "lucide-react";
import api, { resolveImg, formatApiError } from "@/lib/api";
import { formatKWD } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { StaffLangToggle } from "@/components/StaffLangToggle";
import { MEASURE_FIELDS, fieldWarn, hardMeasureError, DEFAULT_RANGES, fieldLabel } from "@/lib/measure";

const PAYMENTS = [{ id: "cash", ar: "نقداً", en: "Cash" }, { id: "knet", ar: "كي نت", en: "KNET" }];
const EMPTY_M = { length: "", shoulder: "", chest: "", sleeve: "", hip: "" };

function Choice({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`relative py-2.5 px-2 text-xs border transition-colors ${active ? "bg-[#141110] text-white border-[#141110]" : "bg-white text-[#141110] border-[#C9BCA8] hover:border-[#6B563F]"}`}>
      {active && <Check className="w-3.5 h-3.5 absolute top-1 end-1" strokeWidth={3} />}
      {children}
    </button>
  );
}

function ReprintDialog({ onClose }) {
  const nav = useNavigate();
  const { lang, tr } = useLang();
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/pos/invoices").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto" onClick={onClose}>
      <div className="bg-white max-w-lg mx-auto my-8 w-[calc(100%-2rem)] p-5" onClick={(e) => e.stopPropagation()} data-testid="reprint-dialog">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-medium">{tr("إعادة طباعة فاتورة سابقة", "Reprint a previous receipt")}</h2>
          <button onClick={onClose} data-testid="reprint-close" aria-label={tr("إغلاق", "Close")} className="min-w-[40px] min-h-[40px] flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-text-muted mb-3">{tr("إعادة الطباعة تعرض الفاتورة نفسها ولا تُنشئ عملية بيع جديدة.", "Reprinting shows the same receipt and does not create a new sale.")}</p>
        {!list ? <p className="text-sm text-text-muted">…</p> : list.length === 0 ? <p className="text-sm text-text-muted">{tr("لا توجد فواتير", "No receipts yet")}</p> : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {list.slice(0, 50).map((inv) => (
              <div key={inv.id} className="border border-border p-2.5 flex items-center gap-3 text-sm" data-testid={`reprint-row-${inv.invoice_no}`}>
                <div className="flex-1">
                  <div className="font-medium">#{inv.invoice_no} · <span dir="ltr">{formatKWD(inv.total, lang)}</span>{inv.status === "cancelled" && <span className="text-destructive text-xs"> · {tr("ملغاة", "Cancelled")}</span>}</div>
                  <div className="text-xs text-text-muted">{new Date(inv.created_at).toLocaleString(lang === "en" ? "en-GB" : "ar-KW")}{inv.customer_name ? ` · ${inv.customer_name}` : ""}</div>
                </div>
                <button onClick={() => nav(`/pos/receipt/${inv.id}?from=pos&reprint=1`)} data-testid={`reprint-btn-${inv.invoice_no}`}
                  className="inline-flex items-center gap-1.5 bg-[#141110] text-white px-4 min-h-[44px] text-sm font-semibold">
                  <Printer className="w-4 h-4" /> {tr("إعادة طباعة", "Reprint")}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function POS() {
  const { user, logout } = useAuth();
  const { lang, tr } = useLang();
  const en = lang === "en";
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [saleType, setSaleType] = useState("mto");
  const [m, setM] = useState(EMPTY_M);
  const [notes, setNotes] = useState("");
  const [editIdx, setEditIdx] = useState(null);
  const [cart, setCart] = useState([]);
  const [custName, setCustName] = useState("");
  const [custPhone, setCustPhone] = useState("");
  const [payment, setPayment] = useState("");
  const [delivery, setDelivery] = useState("pickup");
  const [busy, setBusy] = useState(false);
  const [showReprint, setShowReprint] = useState(false);
  const [lastInvoice, setLastInvoice] = useState(() => { try { return JSON.parse(localStorage.getItem("rnf_last_invoice") || "null"); } catch { return null; } });
  const firstMeasureRef = useRef(null);
  const submitRef = useRef(false);

  useEffect(() => { api.get("/pos/products").then((r) => setProducts(r.data)).catch(() => {}); api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  const ranges = { ...DEFAULT_RANGES, ...(settings?.measurement_ranges || {}) };
  const pname = (p) => (en ? p.name_en || p.name_ar : p.name_ar);
  const payLabel = (id) => { const p = PAYMENTS.find((x) => x.id === id); return p ? (en ? p.en : p.ar) : ""; };

  const filtered = products.filter((p) =>
    !q || p.name_ar.includes(q) || (p.name_en || "").toLowerCase().includes(q.toLowerCase()) || p.sku.toLowerCase().includes(q.toLowerCase()));

  const resetDraft = () => { setSel(null); setSaleType("mto"); setM(EMPTY_M); setNotes(""); setEditIdx(null); };

  const addItem = () => {
    if (!sel) { toast.error(tr("اختاري موديلاً أولاً", "Select a model first")); return; }
    if (saleType === "mto") {
      const err = hardMeasureError(m, lang);
      if (err) { toast.error(err); firstMeasureRef.current?.focus(); return; }
    }
    const item = {
      product_id: sel.id, name_ar: sel.name_ar, name_en: sel.name_en, sku: sel.sku, image: sel.images?.[0], price: sel.price,
      sale_type: saleType === "mto" ? "made_to_order" : "ready",
      measurements: saleType === "mto" ? Object.fromEntries(MEASURE_FIELDS.map((f) => [f.key, +m[f.key]])) : null,
      notes: notes.trim(),
    };
    if (editIdx != null) { setCart((c) => c.map((x, i) => (i === editIdx ? item : x))); toast.success(tr("تم تعديل القطعة", "Item updated")); }
    else { setCart((c) => [...c, item]); toast.success(tr("أُضيفت القطعة إلى الفاتورة", "Item added to the invoice")); }
    resetDraft();
  };

  const editItem = (i) => {
    const it = cart[i];
    const prod = products.find((p) => p.id === it.product_id) || { id: it.product_id, name_ar: it.name_ar, name_en: it.name_en, sku: it.sku, images: [it.image], price: it.price };
    setSel(prod); setSaleType(it.sale_type === "ready" ? "ready" : "mto");
    setM(it.measurements ? Object.fromEntries(MEASURE_FIELDS.map((f) => [f.key, String(it.measurements[f.key] ?? "")])) : EMPTY_M);
    setNotes(it.notes || ""); setEditIdx(i);
  };
  const delItem = (i) => { setCart((c) => c.filter((_, idx) => idx !== i)); if (editIdx === i) resetDraft(); };

  const deliveryOpts = [
    { id: "pickup", label: tr("استلام من المحل", "Store pickup"), fee: 0 },
    { id: "local", label: tr("توصيل داخل الكويت", "Delivery in Kuwait"), fee: Number(settings?.local_delivery_fee ?? 3) },
    { id: "intl", label: tr("شحن دولي", "International shipping"), fee: Number(settings?.intl_shipping_fee ?? 15) },
  ];
  const deliveryFee = deliveryOpts.find((d) => d.id === delivery)?.fee || 0;
  const subtotal = Math.round(cart.reduce((s, it) => s + it.price, 0) * 1000) / 1000;
  const total = Math.round((subtotal + deliveryFee) * 1000) / 1000;
  const canIssue = cart.length > 0 && payment;

  const issue = async () => {
    if (submitRef.current) return;
    if (cart.length === 0) { toast.error(tr("أضيفي قطعة واحدة على الأقل", "Add at least one item")); return; }
    if (!payment) { toast.error(tr("اختاري طريقة الدفع", "Select a payment method")); return; }
    submitRef.current = true; setBusy(true);
    try {
      const payload = {
        customer_name: custName, customer_phone: custPhone,
        items: cart.map((it) => ({ product_id: it.product_id, sale_type: it.sale_type, measurements: it.measurements, notes: it.notes })),
        payment_method: payment, delivery_type: delivery,
      };
      const { data } = await api.post("/pos/order", payload);
      const inv = { id: data.id, no: data.invoice_no };
      localStorage.setItem("rnf_last_invoice", JSON.stringify(inv));
      setLastInvoice(inv);
      toast.success(tr(`تمت الفاتورة #${data.invoice_no}`, `Invoice #${data.invoice_no} issued`));
      setCart([]); resetDraft(); setCustName(""); setCustPhone(""); setDelivery("pickup"); setPayment("");
      nav(`/pos/receipt/${data.id}?from=pos`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || tr("تعذّر إصدار الفاتورة، تحققي من البيانات", "Could not issue the invoice, please check the details"));
    } finally { setBusy(false); submitRef.current = false; }
  };

  const hdrBtn = "flex items-center gap-1.5 text-sm bg-white/10 hover:bg-white/20 px-3 min-h-[40px] rounded";

  return (
    <div className="min-h-screen bg-[#EFEAE2]">
      <header className="bg-[#141110] text-white px-4 py-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <img src="/logo-light.png" alt="RNF_Line" className="h-9 w-auto" />
          <span className="text-xs text-white/60">{tr("نقطة البيع", "Point of sale")}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-white/80 text-sm">{user?.name}</span>
          <StaffLangToggle />
          <button onClick={() => setShowReprint(true)} data-testid="pos-reprint-open" className={hdrBtn}><Printer className="w-4 h-4" /> {tr("إعادة طباعة", "Reprint")}</button>
          {lastInvoice && (
            <button onClick={() => nav(`/pos/receipt/${lastInvoice.id}?from=pos&reprint=1`)} data-testid="pos-reprint-last" className={hdrBtn}>
              {tr("آخر فاتورة", "Last receipt")} #{lastInvoice.no}
            </button>
          )}
          <button onClick={() => nav("/pos/closing")} data-testid="pos-closing" className={hdrBtn}><Calculator className="w-4 h-4" /> {tr("الإقفال اليومي", "Daily closing")}</button>
          {user?.role === "owner" && (
            <button onClick={() => nav("/admin")} data-testid="pos-to-admin" className={hdrBtn}><LayoutDashboard className="w-4 h-4" /> {tr("لوحة الأونر", "Owner panel")}</button>
          )}
          <button onClick={async () => { await logout(); nav("/login"); }} data-testid="pos-logout" aria-label={tr("تسجيل الخروج", "Log out")} className="min-w-[40px] min-h-[40px] flex items-center justify-center"><LogOut className="w-4 h-4" /></button>
        </div>
      </header>
      {showReprint && <ReprintDialog onClose={() => setShowReprint(false)} />}

      <div className="grid lg:grid-cols-[1fr_440px] gap-4 p-4">
        <div>
          <div className="relative mb-4">
            <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-text-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("ابحثي بالاسم أو الرمز…", "Search by name or code…")} data-testid="pos-search"
              className="w-full bg-white border border-[#C9BCA8] ps-10 pe-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#6B563F]" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {filtered.map((p) => {
              const active = sel?.id === p.id;
              return (
                <button key={p.id} onClick={() => { setSel(p); setEditIdx(null); }} data-testid={`pos-product-${p.id}`}
                  className={`relative bg-white border-2 text-start transition-colors ${active ? "border-[#6B563F]" : "border-transparent hover:border-[#C9BCA8]"}`}>
                  {active && <span className="absolute top-1.5 end-1.5 z-10 w-6 h-6 rounded-full bg-[#6B563F] text-white flex items-center justify-center"><Check className="w-4 h-4" strokeWidth={3} /></span>}
                  <div className="aspect-[3/4] bg-surface overflow-hidden">
                    {p.images?.[0] && <img src={resolveImg(p.images[0])} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="p-2">
                    <p className="text-xs leading-snug line-clamp-1 text-[#141110]">{pname(p)}</p>
                    <p className="text-xs text-text-muted mt-0.5">{p.sku} · <span dir="ltr">{formatKWD(p.price, lang)}</span></p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-[#C9BCA8] flex flex-col max-h-[calc(100vh-96px)] sticky top-4">
          <div className="px-4 py-3 border-b border-[#C9BCA8] font-medium text-sm bg-[#F7F3EC] flex justify-between">
            <span>{tr("فاتورة جديدة", "New invoice")}</span><span className="text-text-muted" data-testid="pos-cart-count">{cart.length} {tr("قطعة", cart.length === 1 ? "item" : "items")}</span>
          </div>
          <div className="p-3 space-y-3 overflow-y-auto flex-1">
            {sel ? (
              <div className="border border-[#6B563F] p-2 space-y-2" data-testid="pos-draft">
                <div className="flex gap-3">
                  <div className="w-14 h-[72px] bg-surface overflow-hidden"><img src={resolveImg(sel.images?.[0])} className="w-full h-full object-cover" alt="" /></div>
                  <div className="flex-1"><p className="text-sm text-[#141110]">{pname(sel)}</p><p className="text-xs text-text-muted">{sel.sku} · <span dir="ltr">{formatKWD(sel.price, lang)}</span></p></div>
                  <button onClick={resetDraft} aria-label={tr("إزالة", "Remove")} data-testid="pos-draft-clear" className="text-text-muted hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-2 gap-1.5" data-testid="pos-saletype">
                  <Choice active={saleType === "mto"} onClick={() => setSaleType("mto")} testid="pos-type-mto">{tr("تفصيل حسب الطلب", "Made to order")}</Choice>
                  <Choice active={saleType === "ready"} onClick={() => setSaleType("ready")} testid="pos-type-ready">{tr("جاهز", "Ready")}</Choice>
                </div>
                {saleType === "mto" && (
                  <div className="space-y-2">
                    {MEASURE_FIELDS.map((f, idx) => {
                      const warn = fieldWarn(f.key, m[f.key], ranges, lang);
                      return (
                        <div key={f.key}>
                          <label className="text-xs text-text-muted">{fieldLabel(f, lang)} <span className="text-[10px]">({ranges[f.key].min}-{ranges[f.key].max} {tr("سم", "cm")})</span></label>
                          <input ref={idx === 0 ? firstMeasureRef : null} type="number" step="0.25" value={m[f.key]} onChange={(e) => setM({ ...m, [f.key]: e.target.value })} data-testid={`pos-measure-${f.key}`}
                            className={`w-full border px-2 py-1.5 text-sm ${warn ? "border-amber-500" : "border-[#C9BCA8]"}`} />
                          {warn && <p className="text-[10px] text-amber-600" data-testid={`pos-warn-${f.key}`}>{warn}</p>}
                        </div>
                      );
                    })}
                  </div>
                )}
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder={tr("ملاحظات لهذه القطعة (اختياري)", "Notes for this item (optional)")} data-testid="pos-notes" className="w-full border border-[#C9BCA8] px-3 py-2 text-sm" />
                <button onClick={addItem} data-testid="pos-add-item" className="w-full bg-[#6B563F] text-white py-2.5 text-sm flex items-center justify-center gap-1 hover:bg-[#5a4733]">
                  <Plus className="w-4 h-4" /> {editIdx != null ? tr("حفظ تعديل القطعة", "Save item changes") : tr("إضافة القطعة إلى الفاتورة", "Add item to invoice")}
                </button>
              </div>
            ) : (
              <p className="text-text-muted text-xs text-center py-3">{tr("اختاري موديلاً لإضافته إلى الفاتورة", "Select a model to add to the invoice")}</p>
            )}

            {cart.length > 0 && (
              <div className="space-y-1.5" data-testid="pos-cart">
                {cart.map((it, i) => (
                  <div key={i} className="border border-border p-2 text-sm flex items-start gap-2" data-testid={`pos-cart-item-${i}`}>
                    <div className="flex-1">
                      <div className="flex justify-between"><span>{en ? it.name_en || it.name_ar : it.name_ar} <span className="text-[10px] text-accent">{it.sale_type === "ready" ? tr("جاهز", "Ready") : tr("تفصيل", "Made to order")}</span></span><span dir="ltr">{formatKWD(it.price, lang)}</span></div>
                      {it.measurements && <div className="text-[10px] text-text-muted">{MEASURE_FIELDS.map((f) => `${fieldLabel(f, lang)} ${it.measurements[f.key]}`).join(" · ")}</div>}
                      {it.notes && <div className="text-[10px] text-text-muted">{tr("ملاحظات", "Notes")}: {it.notes}</div>}
                    </div>
                    <button onClick={() => editItem(i)} data-testid={`pos-edit-item-${i}`} aria-label={tr("تعديل", "Edit")} className="text-accent"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => delItem(i)} data-testid={`pos-del-item-${i}`} aria-label={tr("حذف", "Delete")} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}

            <input value={custName} onChange={(e) => setCustName(e.target.value)} placeholder={tr("اسم العميلة (اختياري)", "Customer name (optional)")} data-testid="pos-cust-name" className="w-full border border-[#C9BCA8] px-3 py-2 text-sm" />
            <input value={custPhone} onChange={(e) => setCustPhone(e.target.value)} placeholder={tr("رقم الهاتف (اختياري)", "Phone number (optional)")} dir="ltr" data-testid="pos-cust-phone" className="w-full border border-[#C9BCA8] px-3 py-2 text-sm" />

            <div>
              <div className="text-xs text-text-muted mb-1">{tr("طريقة الاستلام (تُحتسب مرة واحدة للفاتورة)", "Delivery method (charged once per invoice)")}</div>
              <div className="grid grid-cols-3 gap-1.5">
                {deliveryOpts.map((d) => (
                  <Choice key={d.id} active={delivery === d.id} onClick={() => setDelivery(d.id)} testid={`pos-delivery-${d.id}`}>{d.label}<br /><span dir="ltr">{formatKWD(d.fee, lang)}</span></Choice>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs text-text-muted mb-1">{tr("طريقة الدفع", "Payment method")}</div>
              <div className="grid grid-cols-2 gap-1.5">
                {PAYMENTS.map((p) => (<Choice key={p.id} active={payment === p.id} onClick={() => setPayment(p.id)} testid={`pos-payment-${p.id}`}>{en ? p.en : p.ar}</Choice>))}
              </div>
              <p className="text-xs text-[#6B563F] mt-1.5" data-testid="pos-selected-payment">{tr("طريقة الدفع المختارة", "Selected payment")}: {payment ? payLabel(payment) : tr("— لم تُحدّد —", "— none —")}</p>
            </div>

            <div className="border-t border-[#C9BCA8] pt-2 space-y-1 text-sm">
              <div className="flex justify-between"><span>{tr("المجموع", "Subtotal")}</span><span dir="ltr" data-testid="pos-subtotal">{formatKWD(subtotal, lang)}</span></div>
              <div className="flex justify-between"><span>{tr("التوصيل", "Delivery")}</span><span dir="ltr" data-testid="pos-delivery-fee">{formatKWD(deliveryFee, lang)}</span></div>
              <div className="flex justify-between text-lg font-medium"><span>{tr("الإجمالي", "Total")}</span><span dir="ltr" data-testid="pos-total">{formatKWD(total, lang)}</span></div>
            </div>
          </div>

          <div className="border-t border-[#C9BCA8] p-3 bg-white">
            <button onClick={issue} disabled={busy || !canIssue} data-testid="pos-complete-sale"
              className={`w-full py-4 text-base font-medium transition-colors ${canIssue && !busy ? "bg-[#141110] text-white hover:bg-black" : "bg-[#B7ADA0] text-white cursor-not-allowed"}`}>
              {busy ? "…" : tr("إصدار وطباعة الفاتورة", "Issue & print receipt")}
            </button>
            {!canIssue && <p className="text-[11px] text-text-muted mt-1.5 text-center">{cart.length === 0 ? tr("أضيفي قطعة واحدة على الأقل", "Add at least one item") : tr("اختاري طريقة الدفع", "Select a payment method")}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
